import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Status } from 'src/app/shared/models/status';
import { AuthService } from 'src/app/shared/services/auth.service';
import { TurnstileService } from 'src/app/shared/services/turnstile.service';
import { TurnstileComponent } from 'src/app/shared/components/turnstile/turnstile.component';
import { NotEmptyInputValidator } from 'src/app/shared/validators/is-not-empty.validator';
import { TopicService } from 'src/app/features/user/components/topic/services/topic.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  @ViewChild('turnstileWidget') turnstileWidget!: TurnstileComponent;

  form!: FormGroup;
  status!: Status;
  hidePassword = true;
  isLoading = false;
  isFormReady = false;
  showError = false;
  errorMessage = '';
  turnstileToken: string | null = null;
  siteKey = environment.turnstileSiteKey;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private topicService: TopicService,
    private turnstileService: TurnstileService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      username: [
        '',
        [Validators.required, Validators.email, new NotEmptyInputValidator()],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(60),
          new NotEmptyInputValidator()
        ],
      ],
    });

    this.isFormReady = !this.form.invalid;

    this.form.statusChanges.subscribe(
      (e) => (this.isFormReady = 'INVALID' != e)
    );

    if (this.authService.isLoggedIn()) {
      const redirectUrl = this.authService.getRedirectUrl();
      if (redirectUrl) {
        this.router.navigateByUrl(redirectUrl).then(() => {
          this.authService.clearRedirectUrl();
        });
      } else {
        this.router.navigate(['user']);
      }
    }
  }

  onTurnstileToken(token: string | null): void {
    this.turnstileToken = token;
    this.turnstileService.setToken(token);
  }

  onSubmit() {
    // Reset error state
    this.showError = false;
    this.errorMessage = '';

    if (this.form.invalid || !this.turnstileToken) return;

    this.isLoading = true;
    this.authService.login(this.form.value).subscribe({
      next: (result: any) => {
        if ('roles' in result) {

          this.updateUnreadMessagesCount();

          // Check if there is a redirect URL
          console.log('Login successful, checking for redirect URL...');
          const redirectUrl = this.authService.getRedirectUrl();
          console.log('Retrieved redirect URL:', redirectUrl);

          if (redirectUrl) {
            console.log('Navigating to redirect URL:', redirectUrl);
            // Navigate to the original page and then clear the saved URL
            this.router.navigateByUrl(redirectUrl).then(() => {
              this.authService.clearRedirectUrl();
            });
          } else {
            // Standard redirect logic
            if (this.authService.isAdmin()) {
              this.router.navigate(['admin']);
            } else {
              this.router.navigate(['user']);
            }
          }
        } else {
          var message = (result as any[])[0];
          if (message) {
            this.showError = true;
            this.errorMessage = message;
          }
        }
      },
      error: (err: any) => {
        this.showError = true;
        if (err.error && err.error.messages && err.error.messages.length > 0) {
          this.errorMessage = err.error.messages
            .map((msg: any) => msg.content)
            .filter((content: string | undefined) => content)
            .join(', ');
          this.isLoading = false;
        } else if (err.error && typeof err.error === 'string') {
          this.errorMessage = err.error;
          this.isLoading = false;
        } else {
          this.errorMessage = err.message || 'An error occurred while logging in.';
          this.isLoading = false;
        }

        this.turnstileWidget?.reset();
        this.turnstileService.clearToken();
        this.turnstileToken = null;
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  isControlInvalid(controlName: string) {
    var control = this.form.controls[controlName];
    return control.touched && control.dirty && control.invalid;
  }

  unreadMessagesCount: number = 0;

  private updateUnreadMessagesCount() {
    if (!this.authService.isLoggedIn()) return;
    this.topicService.checkNewMessages().subscribe({
      next: (result) => {
        // Get current user email first
        this.authService.getCurrentUser().subscribe(user => {
          if (!user) return;

          // Sum all unread messages across topics
          this.unreadMessagesCount = Object.values(result)
            .map((messages: any[]) => messages.filter((msg: any) =>
              !msg.readBy || !msg.readBy.includes(user.id)
            ).length)
            .reduce((sum: number, count: number) => sum + count, 0);
        });
      },
      error: () => {
        this.unreadMessagesCount = 0;
      }
    });
  }
}
